import React, { useState } from "react";
import { motion } from "framer-motion";
import { LIVING_NODES, LIVING_DETAILS, VERTICALS } from "@/lib/perspective";
import { RadarPerspective } from "@/components/charts/RadarPerspective";

const DEFAULT = { lifestyleFit: 84, commute: 72, connectivity: 80, space: 78, community: 82, education: 76, amenities: 85, longTerm: 80 };

export const LivingPerspective = ({ scores = DEFAULT }) => {
  const [selected, setSelected] = useState("commute");
  const detail = LIVING_DETAILS[selected] || LIVING_DETAILS.commute;
  const radarData = VERTICALS.living.criteria.map((c) => ({ criterion: c.label, value: scores[c.key] ?? 70 }));
  const nodeKeys = ["lifestyleFit", "community", "education", "amenities", "space", "commute", "connectivity", "longTerm"];

  return (
    <div data-testid="living-perspective" className="grid gap-10 lg:grid-cols-2">
      <div className="relative mx-auto flex aspect-square w-full max-w-[440px] items-center justify-center">
        <div className="absolute h-[78%] w-[78%] rounded-full border border-dashed border-[var(--border-subtle)]" />
        <div className="absolute h-[52%] w-[52%] rounded-full border border-[var(--border-subtle)]" />
        <motion.div
          className="relative z-10 flex h-32 w-32 flex-col items-center justify-center rounded-full text-center"
          style={{ background: "linear-gradient(135deg,var(--accent-gold),#0F4A36)", color: "#071711" }}
          initial={{ scale: 0.6, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="text-[10px] uppercase tracking-[0.2em]">Your</span>
          <span className="font-serif text-2xl font-medium">Life</span>
        </motion.div>

        {LIVING_NODES.map((node, i) => {
          const angle = (i / LIVING_NODES.length) * Math.PI * 2 - Math.PI / 2;
          const radius = 42;
          const x = 50 + radius * Math.cos(angle);
          const y = 50 + radius * Math.sin(angle);
          const key = nodeKeys[i];
          return (
            <motion.button
              key={node}
              onClick={() => LIVING_DETAILS[key] && setSelected(key)}
              data-testid={`living-node-${node.toLowerCase().replace(/\s+/g, "-")}`}
              className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border px-3 py-1.5 text-xs font-medium backdrop-blur transition ${selected === key ? "border-[var(--accent-gold)] text-[var(--accent-gold)]" : "border-[var(--border-subtle)] text-[var(--text-primary)] hover:border-[var(--accent-gold)]"}`}
              style={{ left: `${x}%`, top: `${y}%`, background: "var(--glass-bg)" }}
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
            >
              {node}
            </motion.button>
          );
        })}
      </div>

      <div className="flex flex-col justify-center">
        <div className="overline">Living Perspective</div>
        <motion.div
          key={selected}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-3"
        >
          <h3 className="font-serif text-3xl font-light text-[var(--text-primary)]">{detail.title}</h3>
          <p className="mt-1 font-serif text-lg italic text-[var(--accent-gold)]">{detail.lead}</p>
          <p className="mt-4 text-sm text-[var(--text-muted)]">Archedia evaluates:</p>
          <ul className="mt-3 grid grid-cols-2 gap-2">
            {detail.points.map((p) => (
              <li key={p} className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-gold)]" /> {p}
              </li>
            ))}
          </ul>
        </motion.div>

        <div className="mt-8 arch-card p-4">
          <RadarPerspective data={radarData} height={280} />
        </div>
        <p className="mt-5 font-serif text-xl font-light leading-snug text-[var(--text-primary)]">
          A home isn't evaluated only by where it is. It's evaluated by how well it fits your life.
        </p>
      </div>
    </div>
  );
};

export default LivingPerspective;
